import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { Check, ExternalLink, Star, Ban } from '@/components/ui/icons';
import type { CustomField, CustomFieldValue, Task } from '@shared/types';
import { setCustomField } from '@/lib/actions';
import { formatDue, formatMoney } from '@/lib/format';
import { readableOn, rgba } from '@/lib/color';
import { Popover } from '@/components/ui/Popover';
import { MenuSearch } from '@/components/ui/Menu';
import { AvatarStack } from '@/components/ui/Avatar';
import { PeopleMenu } from './People';
import { DateMenu } from './DatePicker';

function OptionPill({ name, color }: { name: string; color: string }) {
  return (
    <span className="option-pill" style={{ background: color, color: readableOn(color) }}>
      <span className="truncate">{name}</span>
    </span>
  );
}

function OptionsMenu({ field, value, multi, onChange }: { field: CustomField; value: string[]; multi: boolean; onChange: (ids: string[]) => void }) {
  const [q, setQ] = useState('');
  const options = useMemo(
    () => [...(field.options ?? [])].sort((a, b) => a.orderIndex - b.orderIndex).filter((o) => !q || o.name.toLowerCase().includes(q.toLowerCase())),
    [field.options, q],
  );
  return (
    <div className="menu" style={{ width: 260 }}>
      <MenuSearch value={q} onChange={setQ} placeholder="Search..." />
      {!multi && (
        <button className="menu-item" onClick={() => onChange([])}>
          <span className="menu-item-icon">
            <Ban size={14} />
          </span>
          <span className="menu-item-label faint">–</span>
        </button>
      )}
      {options.map((o) => {
        const on = value.includes(o.id);
        return (
          <button
            key={o.id}
            className={clsx('menu-item', { selected: on && !multi })}
            onClick={() => onChange(multi ? (on ? value.filter((v) => v !== o.id) : [...value, o.id]) : [o.id])}
          >
            {multi ? (
              <span className="option-label" style={{ background: rgba(o.color, 0.25), color: o.color }}>
                {o.name}
              </span>
            ) : (
              <OptionPill name={o.name} color={o.color} />
            )}
            <span className="spacer" />
            {on && <Check size={14} className="faint" />}
          </button>
        );
      })}
      {options.length === 0 && <div className="menu-empty">No options</div>}
    </div>
  );
}

/** Read-only rendering of a custom field value. */
export function CustomFieldDisplay({ field, value }: { field: CustomField; value: CustomFieldValue | undefined }) {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
    if (field.type === 'checkbox') return <input type="checkbox" className="checkbox" checked={false} readOnly />;
    return <span className="empty-dash">–</span>;
  }
  switch (field.type) {
    case 'dropdown': {
      const o = field.options?.find((x) => x.id === value);
      return o ? <OptionPill name={o.name} color={o.color} /> : <span className="empty-dash">–</span>;
    }
    case 'labels': {
      const ids = value as string[];
      return (
        <span className="labels-row">
          {ids.map((id) => {
            const o = field.options?.find((x) => x.id === id);
            return o ? (
              <span key={id} className="option-label" style={{ background: rgba(o.color, 0.25), color: o.color }}>
                {o.name}
              </span>
            ) : null;
          })}
        </span>
      );
    }
    case 'checkbox':
      return <input type="checkbox" className="checkbox" checked={value === true} readOnly />;
    case 'date':
      return <span>{formatDue(value as number)}</span>;
    case 'money':
      return <span>{formatMoney(Number(value), field.config?.currency ?? 'USD', field.config?.precision ?? 2)}</span>;
    case 'number':
      return <span>{Number(value).toLocaleString()}</span>;
    case 'people':
      return <AvatarStack userIds={value as string[]} size={22} />;
    case 'rating': {
      const max = field.config?.ratingMax ?? 5;
      return (
        <span className="rating">
          {Array.from({ length: max }, (_, i) => (
            <Star key={i} size={13} fill={i < Number(value) ? '#f8ae00' : 'none'} color={i < Number(value) ? '#f8ae00' : 'var(--text-4)'} />
          ))}
        </span>
      );
    }
    case 'progress':
      return (
        <span className="progress-cell">
          <span className="progress-bar">
            <span style={{ width: `${Math.min(100, Number(value))}%` }} />
          </span>
          <span className="faint">{Number(value)}%</span>
        </span>
      );
    case 'url':
      return (
        <a className="link truncate" href={String(value)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
          {String(value).replace(/^https?:\/\//, '')}
          <ExternalLink size={11} />
        </a>
      );
    default:
      return <span className="truncate">{String(value)}</span>;
  }
}

function TextEditor({
  field,
  value,
  onCommit,
  close,
}: {
  field: CustomField;
  value: CustomFieldValue | undefined;
  onCommit: (v: CustomFieldValue) => void;
  close: () => void;
}) {
  const initial = value === undefined || value === null ? '' : String(value);
  const [v, setV] = useState(initial);
  const numeric = field.type === 'number' || field.type === 'money' || field.type === 'progress';
  const commit = () => {
    if (v === initial) return;
    if (v.trim() === '') return onCommit(null);
    if (numeric) {
      const n = Number(v);
      if (!Number.isNaN(n)) onCommit(n);
      return;
    }
    onCommit(v.trim());
  };
  return (
    <div className="menu" style={{ width: 260 }}>
      {field.type === 'textarea' ? (
        <textarea
          className="input"
          style={{ height: 100, padding: 8, resize: 'vertical' }}
          autoFocus
          value={v}
          onChange={(e) => setV(e.target.value)}
          onBlur={commit}
        />
      ) : (
        <input
          className="input"
          autoFocus
          type={numeric ? 'number' : field.type === 'email' ? 'email' : 'text'}
          placeholder={field.type === 'url' ? 'https://' : `Enter ${field.name}`}
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit();
              close();
            }
          }}
          onBlur={commit}
        />
      )}
    </div>
  );
}

/** Editable cell for a custom field; used in list/table rows and the task modal. */
export function CustomFieldCell({ task, field, className }: { task: Task; field: CustomField; className?: string }) {
  const value = task.customFields[field.id];
  if (field.type === 'checkbox') {
    return (
      <span className={clsx('cell-inner', className)}>
        <input
          type="checkbox"
          className="checkbox"
          checked={value === true}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setCustomField(task.id, field, e.target.checked ? true : null)}
        />
      </span>
    );
  }
  if (field.type === 'rating') {
    const max = field.config?.ratingMax ?? 5;
    return (
      <span className={clsx('cell-inner rating editable', className)} onClick={(e) => e.stopPropagation()}>
        {Array.from({ length: max }, (_, i) => (
          <button key={i} onClick={() => setCustomField(task.id, field, Number(value) === i + 1 ? null : i + 1)}>
            <Star size={14} fill={i < Number(value ?? 0) ? '#f8ae00' : 'none'} color={i < Number(value ?? 0) ? '#f8ae00' : 'var(--text-4)'} />
          </button>
        ))}
      </span>
    );
  }
  return (
    <Popover
      placement="bottom-start"
      content={(close) => {
        switch (field.type) {
          case 'dropdown':
            return (
              <OptionsMenu
                field={field}
                value={value ? [String(value)] : []}
                multi={false}
                onChange={(ids) => {
                  setCustomField(task.id, field, ids[0] ?? null);
                  close();
                }}
              />
            );
          case 'labels':
            return <OptionsMenu field={field} value={(value as string[]) ?? []} multi onChange={(ids) => setCustomField(task.id, field, ids)} />;
          case 'people':
            return <PeopleMenu title={field.name} selected={(value as string[]) ?? []} onChange={(ids) => setCustomField(task.id, field, ids)} />;
          case 'date':
            return <DateMenu value={(value as number) ?? null} onChange={(v) => setCustomField(task.id, field, v)} onClose={close} />;
          default:
            return <TextEditor field={field} value={value} close={close} onCommit={(v) => setCustomField(task.id, field, v)} />;
        }
      }}
    >
      <button className={clsx('cell-inner cell-button', className)}>
        <CustomFieldDisplay field={field} value={value} />
      </button>
    </Popover>
  );
}
